import { z } from 'zod';

/**
 * Registry of every feature flag the platform evaluates through OpenFeature.
 *
 * The zod enum is the single source of truth for flag keys: compile-time
 * safety comes from the inferred `FeatureFlagKey` union, runtime safety from
 * parsing untrusted input (e.g. tRPC procedure input) with the schema.
 *
 * Keys must match the flag key configured in every provider backend (Datadog
 * Feature Flags, the local flagd definition file in `api-stack/flagd/`, and
 * any in-memory test configuration).
 */
export const featureFlagKeySchema = z.enum([
  'easter_eggs',
  'object_toolbar',
  'indicator_results_chart_enhancements',
  'indicator_widget_tolerances',
  'appetite_links',
  'attach_file_custom_field',
  'attestation_improvements',
  'cds_enterprise_risk',
  'conditional_fields',
  'custom_attribute_alternate_values',
  'day_attestations',
  'empty_default_dashboard',
  'form_sections',
  'issue-allica',
  'issue-gc',
  'pdf_export',
  'policy_auto_draft',
  'scoring_settings_data',
  'trpc',
  'viewers',
  'statuses',
  'statuses_risk',
  'statuses_control',
  'statuses_operational',
  'statuses_compliance',
  'statuses_platform_surfaces',
  'risk_score_history',
  'notification_test_send',
  'smart_copy_indicator',
  'smart_copy_control_test_result',
  'g2_review_prompt',
  'oversight_rating_performance_entry',
]);

export type FeatureFlagKey = z.infer<typeof featureFlagKeySchema>;

export interface FeatureFlagDefinition {
  /**
   * Value used when the provider is unreachable, the flag is missing from the
   * backend, or evaluation fails. Defaults are deliberately the "feature off"
   * value so a flagging outage never enables unfinished behaviour.
   */
  defaultValue: boolean;
  description: string;
}

export const featureFlagDefinitions: Record<
  FeatureFlagKey,
  FeatureFlagDefinition
> = {
  easter_eggs: {
    defaultValue: false,
    description:
      'Gates hidden easter-egg UI — currently the drag "bounciness" slider on the customisable ribbon filter editor.',
  },
  object_toolbar: {
    defaultValue: false,
    description:
      'Gates the object-level toolbar (CUS-27) that relocates per-object actions (export/delete) plus Save/Cancel into a single header bar. Piloted on the Indicator detail page (CUS-135) before fanning out to the other registers.',
  },
  indicator_results_chart_enhancements: {
    defaultValue: false,
    description:
      'Gates Deliverable 1 of the indicator chart enhancements — the results-tab Actions dropdown, date range picker, chart x-axis/data changes, and PNG export.',
  },
  indicator_widget_tolerances: {
    defaultValue: false,
    description:
      'Gates Deliverable 2 of the indicator chart enhancements — the tolerances/appetites toggle on the dashboard line chart widget.',
  },
  appetite_links: {
    defaultValue: false,
    description: 'Gates linking appetites to other records in the linked-items UI.',
  },
  attach_file_custom_field: {
    defaultValue: false,
    description: 'Gates the Attach File custom field type (CUS-75).',
  },
  attestation_improvements: {
    defaultValue: false,
    description: 'Gates in-progress attestation workflow improvements.',
  },
  cds_enterprise_risk: {
    defaultValue: false,
    description: 'Gates the enterprise-risk custom data source surface.',
  },
  conditional_fields: {
    defaultValue: false,
    description: 'Gates conditional form-field logic (show/hide fields by rule).',
  },
  custom_attribute_alternate_values: {
    defaultValue: false,
    description: 'Gates alternate-value support on custom attributes.',
  },
  day_attestations: {
    defaultValue: false,
    description: 'Legacy Meta.features flag — gates day-based attestation scheduling.',
  },
  empty_default_dashboard: {
    defaultValue: false,
    description: 'Gates rendering an empty default dashboard for new orgs.',
  },
  form_sections: {
    defaultValue: false,
    description: 'Gates sectioned form rendering (grouped form fields).',
  },
  'issue-allica': {
    defaultValue: false,
    description: 'Gates the Allica-specific issue variant.',
  },
  'issue-gc': {
    defaultValue: false,
    description: 'Gates the GC-specific issue variants.',
  },
  pdf_export: {
    defaultValue: false,
    description: 'Gates PDF register export.',
  },
  policy_auto_draft: {
    defaultValue: false,
    description: 'Gates automatic policy draft generation.',
  },
  scoring_settings_data: {
    defaultValue: false,
    description: 'Gates the scoring-settings data configuration surface.',
  },
  trpc: {
    defaultValue: false,
    description:
      'Data-transport switch (ENG-399 Phase 2, ~331 refs). When on, migrated reads/mutations resolve via tRPC v3 instead of GraphQL/Apollo — the per-org toggle behind the createQueryHook / RBAC hook-factory branch on both web and the tRPC backend. Off = the legacy GraphQL path (the safe pre-migration default). This ticket (ENG-408) only registers the key + Datadog targeting; the readers move off Meta.features onto OpenFeature in ENG-540.',
  },
  viewers: {
    defaultValue: false,
    description: 'Gates the viewers role and viewers field on records.',
  },
  statuses: {
    defaultValue: false,
    description:
      'Top-level kill switch (M1) for the unified lifecycle-status rollout. Every per-domain `statuses_*` flag is gated behind this: a domain surface shows lifecycle status only when both `statuses` and its domain flag are on. Migrated from `Meta.features` to OpenFeature/Datadog targeting under ENG-409 (ENG-399 Phase 2).',
  },
  statuses_risk: {
    defaultValue: false,
    description:
      'Gates the unified lifecycle-status UI for the Risk domain (M2): Risks and Risk Ratings. Effective only when `statuses` is also on.',
  },
  statuses_control: {
    defaultValue: false,
    description:
      'Gates the unified lifecycle-status UI for the Control domain (M3): Controls and Control Tests. Effective only when `statuses` is also on.',
  },
  statuses_operational: {
    defaultValue: false,
    description:
      'Gates the unified lifecycle-status UI for the Operational domain (M4): Actions, Issues, Indicators, Appetites, Third Parties, Policies, and Policy Ratings. Effective only when `statuses` is also on.',
  },
  statuses_compliance: {
    defaultValue: false,
    description:
      'Gates the unified lifecycle-status UI for the Compliance domain (M5): Compliance Obligations, Ratings, Assessments, Activities, Internal Audits, and Findings. Effective only when `statuses` is also on.',
  },
  statuses_platform_surfaces: {
    defaultValue: false,
    description:
      'Gates the unified lifecycle-status UI on cross-cutting platform surfaces (M6): registers, dashboards, My Items, pickers, and the customisable data source. Effective only when `statuses` is also on.',
  },
  risk_score_history: {
    defaultValue: false,
    description:
      'Gates the risk Score History tab (RSP-6566) — the per-risk residual/inherent score trend chart and audit-history table.',
  },
  notification_test_send: {
    defaultValue: false,
    description:
      'Gates the notification test-send tooling: the `notifications.test.send` tRPC mutation and its settings-modal UI, which trigger every feed workflow with fixture data to the calling user for template/renderer QA. Non-production only.',
  },
  smart_copy_indicator: {
    defaultValue: false,
    description:
      'Gates the Indicator Smart Copy action (WOR-36) — the Actions-dropdown entry that pre-fills a new indicator result from the previous one. Migrated from a per-org `Meta.features` flag to OpenFeature/Datadog targeting under ENG-586 (ENG-399 Phase 2). This ticket only registers the key + Datadog targeting; the reader (`useIsFeatureFlagEnabled`) stays on Meta.features until a later reader swap.',
  },
  smart_copy_control_test_result: {
    defaultValue: false,
    description:
      'Gates the Control Test Result Smart Copy action (WOR-53) that pre-fills a new control test result from the previous one. Migrated from a per-org `Meta.features` flag to OpenFeature/Datadog targeting under ENG-586 (ENG-399 Phase 2). WOR-53 moved this from the `control.subModules.control_test_smart_copy` customer module onto the per-org flag, back-filling every org that had the module enabled — Datadog targeting must reproduce that combined membership (flag token ∪ module-enabled orgs). This ticket only registers the key + Datadog targeting; the reader (`useIsFeatureFlagEnabled`) stays on Meta.features until a later reader swap.',
  },
  g2_review_prompt: {
    defaultValue: false,
    description:
      'Gates the G2 in-app review prompt (RSP-6610) — the login-time modal embedding the G2 review iframe for RiskManager/StandardEnhanced users, and its backing `g2Review` tRPC procedures. Rolled out per-org via orgId targeting.',
  },
  oversight_rating_performance_entry: {
    defaultValue: false,
    description:
      'Gates the oversight-rating entry point on the control/risk performance screens (RSP-6673). Migrated from a per-org `Meta.features` flag to OpenFeature/Datadog targeting under ENG-586 (ENG-399 Phase 2); unlike the two `smart_copy_*` flags it was never in the OpenFeature registry before. This ticket only registers the key + Datadog targeting; the reader (`useIsFeatureFlagEnabled`) stays on Meta.features until a later reader swap.',
  },
};

export const featureFlagKeys: FeatureFlagKey[] = featureFlagKeySchema.options;
